/* eslint-disable no-param-reassign */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import {
  addMonths,
  addYears,
  eachMonthOfInterval,
  eachYearOfInterval,
} from 'date-fns';
import { MdClose, MdOutlineAddBox } from 'react-icons/md';

import { Grid2 } from '@mui/material';
import api, { authHeader } from '../../../../../../../services/API/apiConfig';

import { DialogBox } from '../../../../../../components/singleComponents/dialogs/DialogBox';
import ToasterBox from '../../../../../../components/singleComponents/toaster/ToasterBox';

import NewSchedule from '../../../../../../components/singleComponents/modals/NewSchedule';
import {
  AddPeriodCellStyled,
  BtnStyled,
  ButtonBoxStyled,
  CellInputStyled,
  CloseIconStyled,
  EmptyTableCellStyled,
  FatherSTableCellStyled,
  HeaderTypographyStyled,
  ItemNumStyled,
  NameStyled,
  OutlinedButtonStyled,
  RemoveIconStyled,
  RootStyled,
  TableCellFatherStyled,
  TableCellHeaderFixedStyled,
  TableCellHeaderFixedTotalStyled,
  TableCellHeaderStickyStyled,
  TableCellHeaderStyled,
  TableCellStyled,
  TableContainerStyled,
  TableRowStyled,
  TitleBoxStyled,
  TitleStyled,
} from './NewTimeline.styles';

export function NewTimeline() {
  const tableRef = useRef(null);
  const navigate = useNavigate();
  const match = useMatch('/new-timeline/:id');

  const [successMessage, setSuccessMessage] = useState('');
  const [successSubmit, setSuccessSubmit] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorSubmit, setErrorSubmit] = useState(false);

  const [constructionData, setConstructionData] = useState({});
  const [scheduleId, setScheduleId] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [frequency, setFrequency] = useState('');
  const [periods, setPeriods] = useState(0);
  const [newPeriods, setNewPeriods] = useState(0);
  const [disableInputs, setDisableInputs] = useState(true);
  const [initialDate, setInitialDate] = useState('');
  const [finalDate, setFinalDate] = useState('');
  const [totalPercentage, setTotalPercentage] = useState([]);
  const [percentageValues, setPercentageValues] = useState([]);
  const [saveData, setSaveData] = useState([]);
  const [headerData, setHeaderData] = useState([]);
  const headerFrequency = headerData.slice(0, periods);
  const [footerData, setFooterData] = useState([]);
  const footerFrequency = footerData.slice(0, periods);

  const [clearDialog, setClearDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenEditDialog = () => setEditDialog(true);
  const handleCloseEditDialog = () => setEditDialog(false);
  const handleOpenClearDialog = () => setClearDialog(true);
  const handleCloseClearDialog = () => setClearDialog(false);

  const periodsArr = [];
  for (let i = 0; i < periods; i += 1) {
    periodsArr.push(i + 1);
  }

  const id = match ? match.params.id : null;

  function handleGoBack() {
    navigate(-1);
  }

  function getHeaderFrequency() {
    const date = new Date(initialDate);
    const periodsNum = periods === '' ? 0 : periods - 1;
    const monthsOptions = [
      'JAN',
      'FEV',
      'MAR',
      'ABR',
      'MAI',
      'JUN',
      'JUL',
      'AGO',
      'SET',
      'OUT',
      'NOV',
      'DEZ',
    ];
    const newHeader = [...headerData];

    if (frequency === 'weekly') {
      periodsArr.forEach((data, i) => {
        newHeader[i] = `SEMANA ${i + 1}`;
      });
    } else if (frequency === 'biweekly') {
      const periodsIteration = periodsNum / 2 + 1;
      const endBiWeek = addMonths(date, periodsIteration);
      const biWeekArray = eachMonthOfInterval({ start: date, end: endBiWeek });
      const tempArr = [];

      biWeekArray.forEach((mon) => {
        tempArr.push(`${monthsOptions[mon.getMonth()]} 1/2`);
        tempArr.push(`${monthsOptions[mon.getMonth()]} 2/2`);
      });
      if (date.getDate() > 15) {
        tempArr.shift();
      }
      periodsArr.forEach((data, i) => {
        newHeader[i] = tempArr[i];
      });
    } else if (frequency === 'monthly') {
      const endMonth = addMonths(date, periodsNum === -1 ? 0 : periodsNum);
      const monthsArray = eachMonthOfInterval({ start: date, end: endMonth });

      monthsArray.forEach((mon, idx) => {
        newHeader[idx] =
          `${monthsOptions[mon.getMonth()]} / ${mon.getFullYear()}`;
      });
    } else if (frequency === 'quarterly') {
      const endQuarterly = addMonths(date, periods * 3);
      const quarterlyArray = eachMonthOfInterval({
        start: date,
        end: endQuarterly,
      });
      const quarters = [];
      const totalMonths = [];

      quarterlyArray.forEach((data, i) => {
        totalMonths.push(monthsOptions[quarterlyArray[i].getMonth()]);
      });
      for (let b = 0; b < totalMonths.length; b += 3) {
        const chunk = totalMonths.slice(b, b + 3);
        quarters.push(chunk);
      }
      quarters.forEach((data, i) => {
        newHeader[i] = data.join('/');
      });
    } else if (frequency === 'annualy') {
      const endYears = addYears(date, periodsNum === -1 ? 0 : periodsNum);
      const yearsArray = eachYearOfInterval({ start: date, end: endYears });

      yearsArray.forEach((data, i) => {
        newHeader[i] = data.getFullYear();
      });
    }
    setHeaderData(newHeader);
  }

  function getFooterFrequency() {
    if (periods > footerData.length) {
      for (let i = footerData.length; i < periods; i += 1) {
        footerData.push({ period: i + 1, total: 0 });
      }
    }
  }

  const getDataCallback = useCallback(async () => {
    await api
      .get(`/constructions/${id}/details?budget=false`, {
        headers: authHeader(),
      })
      .then((res) => {
        const { data } = res.data;
        setConstructionData(data);
      });

    await api
      .get(`/constructions/${id}/schedules`, {
        headers: authHeader(),
      })
      .then((res) => {
        const { data } = res.data;

        const itemNumbers = data.schedule_items.map((item) => item.item_num);
        data.schedule_items.forEach((item) => {
          item.isLeaf = !itemNumbers.some((num) =>
            num.startsWith(`${item.item_num}.`)
          );
        });

        setScheduleId(data.id);
        setTableData(data.schedule_items);
        setFrequency(data.frequency);
        setFooterData(data.budget_per_period);
        setPeriods(data.periods);
        const convertedDate = data.starts_at.split('/').reverse().join('/');
        setInitialDate(convertedDate);

        const savedItems = [...percentageValues];
        const newPercentage = [...totalPercentage];
        // creates an array for each data.schedule_items with the values from schedule_data
        data.schedule_items?.forEach((scheduleItem, scheduleIndx) => {
          scheduleItem.schedule_data?.forEach((schedulePeriod, itemIndx) => {
            // if the values in schedule_data.period are not linear, adds 0 in the missing periods
            const periodIdx = itemIndx + 1;
            const itemsIndex = schedulePeriod.period - 1;

            if (schedulePeriod.period > periodIdx && savedItems[scheduleIndx]) {
              for (let b = 0; b < itemsIndex; b += 1) {
                if (savedItems[scheduleIndx][b] === null) {
                  savedItems[scheduleIndx][b] = 0;
                }
              }
              savedItems[scheduleIndx][itemsIndex] = schedulePeriod.percentage;
            } else if (savedItems.length) {
              savedItems[scheduleIndx][itemIndx] = schedulePeriod.percentage;
            }
          });

          if (savedItems.length) {
            newPercentage[scheduleIndx] = savedItems[scheduleIndx].reduce(
              (p, c) => p + c
            );
          }
        });

        const percentages = data.schedule_items.map((entry) => {
          const rowValues = new Array(data.periods).fill(0);
          entry.schedule_data.forEach((value) => {
            rowValues[value.period - 1] = value.percentage;
          });
          return rowValues;
        });

        const totalPercentages = percentages.reduce((acc, curr) => {
          const rowTotal = curr.reduce((acc2, curr2) => acc2 + curr2);
          return [...acc, rowTotal];
        }, []);

        setPercentageValues(percentages);
        setTotalPercentage(totalPercentages);
      });
  }, []);

  useEffect(() => {
    getDataCallback();
  }, [getDataCallback]);

  useEffect(() => {
    if (initialDate !== '' && +initialDate.slice(0, 4) >= 2000) {
      getHeaderFrequency();
    }
    if (footerData.length > 0) {
      getFooterFrequency();
    }
  }, [periods, frequency, initialDate]);

  const navigateBack = () => {
    handleGoBack();
  };

  const handleFrequency = (e) => {
    setFrequency(e.target.value);
  };

  const childStyle = (num) => num.includes('.');

  function getTableRowClass(num) {
    const dots = num.split('').filter((character) => character === '.').length;

    if (dots === 0) return 'primary-item';
    if (dots === 1) return 'secondary-item';
    return 'tertiary-item';
  }

  const getPercentageStyle = (idxRow) => {
    if (totalPercentage[idxRow] === 100) {
      return { color: '#508239', fontWeight: 600 };
    }
    if (totalPercentage[idxRow] > 100) {
      return { color: '#F51F03', fontWeight: 600 };
    }
    if (totalPercentage[idxRow] > 0) {
      return { color: '#EB7D36', fontWeight: 600 };
    }
    return {};
  };

  const clearPercentageValues = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const day = today.getDate();
    setInitialDate(
      `${year}/${month > 9 ? month : `0${month}`}/${day > 9 ? day : `0${day}`}`
    );

    const scheduleData = tableData.flatMap((scheduleItem) => {
      const schedule = scheduleItem.schedule_data.map((item) => ({
        ...item,
        percentage: 0,
      }));
      return schedule;
    });
    setSaveData(scheduleData);

    for (let index = 0; index < footerData.length; index += 1) {
      footerData[index] = '0.0';
    }

    setPercentageValues([]);
    setTotalPercentage([]);
    setDisableInputs(false);
    handleCloseClearDialog();
    setModalOpen(true);
  };

  const saveSchedule = () => {
    /*
    loops through saveData and adds item._destroy in the case periods was altered
    and there are still items with a period that no longer exists
    */
    const scheduleAttributes = saveData.map((item) => {
      if (item.period > periods) {
        return { ...item, _destroy: true };
      }
      return item;
    });

    const data = {
      schedule: {
        frequency,
        periods,
        starts_at: initialDate.split('/').reverse().join('/'),
        schedule_items_attributes: scheduleAttributes,
      },
    };

    api
      .patch(`/schedules/${scheduleId}`, data, {
        headers: authHeader(),
      })
      .then((res) => {
        if (res.data.status === 0) {
          setSuccessMessage('Cronograma salvo');
          setSuccessSubmit(true);

          setTimeout(() => {
            handleGoBack();
          }, 2000);
          return;
        }
        setErrorMessage('Algo deu errado, tente novamente.');
        setErrorSubmit(true);
      });
  };

  const handlePercentageChange = (percentage, idxRow, i, row) => {
    const adjustedPercentage = percentage.replace(/[^0-9]/g, '0');

    const newItems = [...percentageValues];
    newItems[idxRow][i] = Number(adjustedPercentage);
    setPercentageValues(newItems);

    if (percentageValues.length > 0) {
      const newPercentage = [...totalPercentage];
      newPercentage[idxRow] = percentageValues[idxRow].reduce(
        (p, c) => Number(p) + Number(c) || 0
      );
      setTotalPercentage(newPercentage);
    }

    const newPeriod = i + 1;

    // checks if the item was added to saveData before
    const saveDataItem = saveData.find(
      (element) => element.item_id === row.id && element.period === newPeriod
    );

    // sets saveDataItem percentage, updates saveData and return
    if (saveDataItem) {
      saveDataItem.percentage = Number(adjustedPercentage);
      const newData = [...saveData];
      setSaveData(newData);
      return;
    }

    // checks if the row already has the item
    const rowItem = row.schedule_data.find(
      (element) => element.item_id === row.id && element.period === newPeriod
    );

    // if so, sets saveData with the id, else without the id
    if (rowItem) {
      row.schedule_data.forEach((data) => {
        if (data.period === newPeriod) {
          const newData = [
            ...saveData,
            {
              id: data.id,
              item_id: row.id,
              period: newPeriod,
              percentage: Number(adjustedPercentage),
            },
          ];
          setSaveData(newData);
        }
      });
    } else {
      const newData = [
        ...saveData,
        {
          item_id: row.id,
          period: newPeriod,
          percentage: Number(adjustedPercentage),
        },
      ];
      setSaveData(newData);
    }
  };

  const addPeriod = () => {
    const newPercentages = [...percentageValues];
    newPercentages.forEach((item) => {
      item.push(0);
    });

    setPercentageValues(newPercentages);
    setPeriods((prev) => prev + 1);
    setNewPeriods((prev) => prev + 1);

    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.scrollLeft = tableRef.current.scrollWidth;
      }
    }, 100);
  };

  const removePeriod = () => {
    const newPercentages = [...percentageValues];
    newPercentages.forEach((item) => {
      item.pop();
    });

    const newTotal = newPercentages.map((item) =>
      item.reduce((curr, acc) => curr + acc)
    );

    setTotalPercentage(newTotal);
    setPercentageValues(newPercentages);
    setPeriods((prev) => prev - 1);
    setNewPeriods((prev) => prev - 1);
  };

  return (
    <RootStyled>
      {successSubmit && <ToasterBox message={successMessage} />}
      {errorSubmit && <ToasterBox message={errorMessage} />}
      <TitleBoxStyled>
        <TitleStyled variant="h1">
          {id !== null ? 'Editar cronograma' : 'Novo cronograma'}
        </TitleStyled>
        <CloseIconStyled onClick={handleOpenEditDialog} size="large">
          <MdClose />
        </CloseIconStyled>
        <DialogBox
          title="Cancelar edição de cronograma"
          content="Deseja realmente cancelar a edição? Todas as informações inseridas serão perdidas."
          openDialog={editDialog}
          closeDialog={handleCloseEditDialog}
          mainAction={navigateBack}
        />
      </TitleBoxStyled>
      <Box>
        <NameStyled variant="h3">
          {constructionData ? constructionData.name : ''}
        </NameStyled>
        <HeaderTypographyStyled>
          {constructionData ? constructionData.square_meters : ''} m²
        </HeaderTypographyStyled>
        <HeaderTypographyStyled>
          Total orçado:{' '}
          <span style={{ color: '#64A348' }}>
            {constructionData
              ? new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(constructionData.total_budget)
              : ''}
          </span>
        </HeaderTypographyStyled>
        <HeaderTypographyStyled>
          Total/m² orçado:{' '}
          <span style={{ color: '#64A348' }}>
            {constructionData
              ? new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(constructionData.total_budget_per_m2)
              : ''}
          </span>
        </HeaderTypographyStyled>
      </Box>
      <ButtonBoxStyled>
        <OutlinedButtonStyled
          variant="outlined"
          onClick={handleOpenClearDialog}
        >
          Redefinir cronograma
        </OutlinedButtonStyled>
        <DialogBox
          title="Redefinir cronograma"
          content="Deseja realmente redefinir o cronograma? Todas as informações inseridas serão perdidas."
          openDialog={clearDialog}
          closeDialog={handleCloseClearDialog}
          mainAction={clearPercentageValues}
        />
        <BtnStyled onClick={saveSchedule}>Salvar cronograma</BtnStyled>
      </ButtonBoxStyled>
      <TableContainerStyled ref={tableRef}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCellHeaderStickyStyled>
                ETAPAS DO PROJETO
              </TableCellHeaderStickyStyled>
              <TableCellHeaderFixedTotalStyled>
                %
              </TableCellHeaderFixedTotalStyled>
              {headerFrequency.map((data, index, array) => (
                <TableCellHeaderStyled key={data}>
                  <Grid2
                    container
                    justifyContent="space-between"
                    sx={{ px: 2 }}
                  >
                    <Grid2 item>{data}</Grid2>
                    {index === array.length - 1 && newPeriods > 0 && (
                      <Grid2 item title="Clique no ícone para remover a coluna">
                        <RemoveIconStyled onClick={removePeriod} />
                      </Grid2>
                    )}
                  </Grid2>
                </TableCellHeaderStyled>
              ))}
              <AddPeriodCellStyled
                onClick={addPeriod}
                title="Clique para adicionar uma nova coluna"
              >
                <MdOutlineAddBox style={{ color: 'blue' }} />
              </AddPeriodCellStyled>
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData &&
              tableData.map((row, idxRow) => {
                if (percentageValues.length < 1) {
                  for (let a = 0; a < tableData.length; a += 1) {
                    percentageValues.push([]);
                  }
                }
                return (
                  <TableRowStyled
                    key={row.id}
                    className={getTableRowClass(row.item_num)}
                  >
                    <TableCellStyled childStyle={childStyle(row.item_num)}>
                      <ItemNumStyled>{row.item_num}</ItemNumStyled>
                      {row.description}
                    </TableCellStyled>
                    <TableCellFatherStyled
                      childStyle={childStyle(row.item_num)}
                    >
                      <span style={getPercentageStyle(idxRow)}>
                        {row.isLeaf ? `${totalPercentage[idxRow] || 0} %` : ''}
                      </span>
                    </TableCellFatherStyled>
                    {periodsArr.map((_cell, i) => {
                      if (percentageValues[idxRow].length < 1) {
                        for (let b = 0; b < periods; b += 1) {
                          percentageValues[idxRow].push(0);
                        }
                      }
                      return (
                        <FatherSTableCellStyled
                          childStyle={childStyle(row.item_num)}
                        >
                          {row.isLeaf && (
                            <CellInputStyled
                              type="tel"
                              inputProps={{ maxLength: '3' }}
                              endAdornment="%"
                              value={
                                percentageValues[idxRow].length > 0
                                  ? percentageValues[idxRow][i]
                                  : ''
                              }
                              onChange={(e) =>
                                handlePercentageChange(
                                  e.target.value,
                                  idxRow,
                                  i,
                                  row
                                )
                              }
                            />
                          )}
                        </FatherSTableCellStyled>
                      );
                    })}
                    <EmptyTableCellStyled style={{ background: '#ffffff' }} />
                  </TableRowStyled>
                );
              })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCellHeaderFixedStyled colSpan={2}>
                ORÇAMENTO DO PERÍODO
              </TableCellHeaderFixedStyled>
              {footerFrequency?.map((budget) => (
                <TableCellHeaderStyled key={budget.period}>
                  R$ {budget.total}
                </TableCellHeaderStyled>
              ))}
              <EmptyTableCellStyled />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainerStyled>
      <NewSchedule
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        disableInputs={disableInputs}
        frequency={frequency}
        handleFrequency={handleFrequency}
        periods={periods}
        setPeriods={setPeriods}
        percentageValues={percentageValues}
        initialDate={initialDate}
        setInitialDate={setInitialDate}
        finalDate={finalDate}
        setFinalDate={setFinalDate}
        saveSchedule={saveSchedule}
        clearPercentageValues={clearPercentageValues}
      />
    </RootStyled>
  );
}
